/**
 * Currency formatting utilities for the lending system
 */

/**
 * Check if price masking is enabled from localStorage
 * This allows the utility to work outside of React context if needed
 */
const isPriceMasked = (): boolean => {
  if (typeof window === 'undefined') return false;
  const saved = localStorage.getItem('price_masking_enabled');
  return saved === 'true';
};

/**
 * Get currency symbol for masking display
 */
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
  };
  return symbols[currency] || currency;
};

/**
 * Format currency with optional masking
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'INR')
 * @param forceUnmask - Force unmask even if masking is enabled (for admin/internal use)
 */
export const formatCurrency = (amount: number, currency: string = 'INR', forceUnmask: boolean = false): string => {
  // Check if masking is enabled and not forced to unmask
  if (!forceUnmask && isPriceMasked()) {
    // Return masked value - show currency symbol but mask the number
    const currencySymbol = getCurrencySymbol(currency);
    return `${currencySymbol}****`;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format currency in compact notation with optional masking
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'INR')
 * @param forceUnmask - Force unmask even if masking is enabled (for admin/internal use)
 */
export const formatCurrencyCompact = (amount: number, currency: string = 'INR', forceUnmask: boolean = false): string => {
  // Check if masking is enabled and not forced to unmask
  if (!forceUnmask && isPriceMasked()) {
    const currencySymbol = getCurrencySymbol(currency);
    return `${currencySymbol}****`;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(amount);
};

export const parseCurrency = (currencyString: string): number => {
  // Remove currency symbols and parse as number
  const cleaned = currencyString.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
};

export const formatPercentage = (value: number, decimals: number = 2): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatNumber = (value: number, decimals: number = 2): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

export const formatNumberCompact = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};
